<template>
  <div class="container" :style="{width:width+'px',height:height+'px','background-image':blur ? '' : 'url('+this.profilepic.url+')'}">
    <transition-group name="fade">
      <div key="thumb" class="window" :class="{blur}" :style="{'background-image':'url('+thumbnailUrl+')'}" v-show="!isPlaying && !isWaitingToPlay">
        <div class="overlay-center">
          <v-icon :size="height*0.4">mdi-check-circle-outline</v-icon>
        </div>
      </div>
      <div key="vid" class="window" v-show="isPlaying && responseVideoHasUrl">
        <v-progress-circular :indeterminate="true" color="white" v-if="loading" class="centered"></v-progress-circular>
        <video ref="video" :src="responseVideoUrl" :width="width" :height="height" :class="{blur}"></video>
      </div>
      <div key="vid2" class="window" v-show="isWaitingToPlay && !responseVideoHasUrl">
        <div class="overlay-center">
          <span><v-progress-circular :indeterminate="true" color="white"></v-progress-circular>
          <br>
          <br>
          <span>
             {{$t('components.video_playback.preparing')}}
          </span>
          </span>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
  import {cacheUrl} from "../../../lib/cache";

  export default {
    name: "VideoPlayback",
    props:{
      responsevideo:{

      },
      width:{
        default:640
      },
      height:{
        default:480
      },
      blur:{
        default:false
      }
    },
    data(){
      return {
        thumbnailUrl : null,
        isWaitingToPlay: false,
        isPlaying: false,
        progressInterval: null,
        videoRetrieverInterval: null,
        loading: true
      };
    },
    computed:{
      profilepic(){
        return this.$store.state.assessment.assessment.profilepic;
      },
      responseVideoHasUrl(){
        return (this.responsevideo && this.responsevideo.localfile && this.responsevideo.localfile.url)
                || (this.responsevideo && this.responsevideo.audio && this.responsevideo.audio.url)
                || (this.responsevideo && this.responsevideo.file && this.responsevideo.file.url);
      },
      videoOnlyHasAudio(){
        return this.responsevideo && this.responsevideo.audio && this.responsevideo.audio.url && (
                !(this.responsevideo.file && this.responsevideo.file.url) &&
                !(this.responsevideo.localfile && this.responsevideo.localfile.url)
        );
      },
      responseVideoUrl(){
        if(!this.responseVideoHasUrl){
          return null;
        }

        if(this.responsevideo.file && this.responsevideo.file.url){
            return this.responsevideo.file.internal_url || this.responsevideo.file.url;
        }else if(this.responsevideo.localfile && this.responsevideo.localfile.url){
            return this.responsevideo.localfile.internal_url || this.responsevideo.localfile.url;
        }else if(this.responsevideo.audio && this.responsevideo.audio.url) {
            return this.responsevideo.audio.internal_url || this.responsevideo.audio.url;
        }

        return null;
      }
    },
    created(){
      this.watchResponsevideo();
    },
    mounted(){
      this.loadThumbnail();
    },
    methods:{
      watchResponsevideo(){
        this.$watch('responsevideo',()=>{
          this.loadThumbnail();
        },{deep:true});
      },
      loadThumbnail(){
        if(this.responsevideo.thumbnail && this.responsevideo.thumbnail.url && this.thumbnailUrl!==this.responsevideo.thumbnail.url){
          cacheUrl(this.responsevideo.thumbnail.url).then(()=>{
            this.thumbnailUrl = this.responsevideo.thumbnail.url;
          });
        }else if(this.thumbnailUrl!==this.responsevideo.thumbnail.url){
          this.thumbnailUrl=null;
        }
      },
      play(){
        this.isPlaying=true;
        if(!this.responseVideoHasUrl){
          this.isWaitingToPlay=true;
          this.emitWaiting();
          return;
        }
        this.isWaitingToPlay=false;
        this.emitWaiting();
        this.$refs.video.play();
        this.emitPlaybackStarted();
        this.startProgress();
        this.$refs.video.addEventListener('waiting',this.onPlaybackBuffering);
        this.$refs.video.addEventListener('playing',this.onPlaybackResumed);
        this.$refs.video.addEventListener('ended',this.onStopped);
      },
      emitWaiting(){
        this.$emit('waiting',this.isWaitingToPlay);
      },
      onPlaybackBuffering(){
        this.startBuffering();
      },
      onPlaybackResumed(){
        this.stopBuffering();
        this.emitPlaybackStarted();
      },
      onStopped(){
        this.stopBuffering();
        if(this.getCurrentTime()>=this.getDuration()){
          this.$refs.video.currentTime=0;
        }
        this.isPlaying=false;
        this.isWaitingToPlay=false;
        this.$refs.video.removeEventListener('waiting',this.onPlaybackBuffering);
        this.$refs.video.removeEventListener('playing',this.onPlaybackResumed);
        this.$refs.video.removeEventListener('ended',this.onStopped);
        this.$emit('playback-stopped');
      },
      startBuffering(){
        this.loading=true;
        this.$emit('buffering',true);
      },
      stopBuffering(){
        this.loading=false;
        this.$emit('buffering',false);
      },
      emitPlaybackStarted(){
        const data = {
          duration : Math.round(this.getDuration())
        };
        this.$emit('playback-started',data);
      },
      stop(){
        this.isPlaying=false;
        this.isWaitingToPlay=false;
        this.emitWaiting();
        if(!this.responseVideoHasUrl){
          return;
        }
        this.$refs.video.pause();
        this.onStopped();
      },
      seek(positionPerc){
        this.$refs.video.currentTime=this.getDuration()*positionPerc;
        this.emitProgress();
      },
      getCurrentTime(){
        if(!this.$refs.video) return null;
        return this.$refs.video.currentTime;
      },
      getDuration(){
        if(!this.$refs.video) return null;
        return this.$refs.video.duration;
      },
      getBuffered(){
        if(!this.$refs.video) return null;
        if(this.$refs.video.buffered.length===0) return 0;
        const current=this.getCurrentTime() || 0;
        let diff=1000;
        let closest=0;

        for(let i=0; i<this.$refs.video.buffered.length; i++){
           const start=this.$refs.video.buffered.start(i);
           const x = current - start;
           if(x > 0 && x < diff){
             diff=x;
             closest=i;
           }
        }

        return this.$refs.video.buffered.end(closest);
      },
      emitProgress(){
        const progObj={
          buffered:Math.round(this.getBuffered()),
          progress:Math.round(this.getCurrentTime()),
        };
        this.$emit('progress',progObj);
      },
      startProgress(){
        clearInterval(this.progressInterval);
        this.emitProgress();
        this.progressInterval=setInterval(()=>{
          if(this.getDuration()===null || (this.getBuffered()===this.getDuration() && !this.isPlaying)){
             this.stopProgress();
             this.isPlaying=false;
          }
          this.emitProgress();
        },1000);
      },
      stopProgress(){
        clearInterval(this.progressInterval);
      }
    },
    beforeDestroy() {

    }
  }
</script>

<style scoped>

  .blur{
    -o-filter: blur(25px);
    filter: blur(25px);
  }

  .container{
    position:relative;
    background-color: black;
    border-radius: 10px;
    box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
    overflow: hidden;
    background-size: cover;
    background-repeat: no-repeat
  }

  .container .window{
    position:absolute;
    left:0;
    top:0;
    width:100%;
    height:100%;
    border-radius: 10px;
    transition: background .5s linear;
  }

  .container .overlay-center{
    font-family: Roboto, sans-serif;
    font-size: 14pt;
    color: white;
    background-color: black;
    opacity: 0.6;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    width:100%;
    height:100%;
    text-align: center;
    padding: 10px;
  }

  .centered{
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    width: 50px;
    height: 50px;
  }
</style>